import type { CsimEquipment, CsimRack, DecodedCsimSave, SaveEquipmentKind } from "./csim-save";
import { createPlanFromApiSnapshot, SavegameImportError, type SavegamePlanResult } from "./savegame-import";

export const DEFAULT_GAME_API_URL = "http://127.0.0.1:9875";

const PAGE_SIZE = 1_000;
const MAX_API_ITEMS = 10_000;
const MAX_API_RACKS = 1_000;
const REQUEST_TIMEOUT_MS = 15_000;

export type ApiImportStage = "connecting" | "world" | "racks" | "equipment" | "mapping" | "complete";
export type ApiImportErrorCode =
    | "invalid-url"
    | "unreachable"
    | "http-error"
    | "invalid-response"
    | "too-large"
    | "unsupported-site"
    | "mapping-failed"
    | "worker-failed";

export interface ApiImportProgressMessage {
    readonly type: "progress";
    readonly stage: ApiImportStage;
    readonly percent: number;
}

export interface ApiImportCompleteMessage {
    readonly type: "complete";
    readonly result: SavegamePlanResult;
}

export interface ApiImportErrorMessage {
    readonly type: "error";
    readonly code: ApiImportErrorCode;
}

export type ApiImportWorkerMessage = ApiImportProgressMessage | ApiImportCompleteMessage | ApiImportErrorMessage;

export interface ApiImportWorkerRequest {
    readonly apiBaseUrl: string;
}

interface ApiCollectionDefinition {
    readonly kind: Exclude<SaveEquipmentKind, "edd">;
    readonly path: string;
}

interface ApiSnapshot {
    readonly siteId: string;
    readonly siteName: string;
    readonly racks: readonly Record<string, unknown>[];
    readonly equipment: Readonly<
        Partial<Record<Exclude<SaveEquipmentKind, "edd">, readonly Record<string, unknown>[]>>
    >;
}

const API_COLLECTIONS: readonly ApiCollectionDefinition[] = [
    { kind: "host", path: "/api/v1/hosts" },
    { kind: "switch", path: "/api/v1/network/switches" },
    { kind: "gateway", path: "/api/v1/network/gateways" },
    { kind: "security", path: "/api/v1/network/appliances" },
    { kind: "patch-panel", path: "/api/v1/network/patch-panels" },
];

export class ApiImportError extends Error {
    public readonly code: ApiImportErrorCode;

    public constructor(code: ApiImportErrorCode, message: string) {
        super(message);
        this.name = "ApiImportError";
        this.code = code;
    }
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === "object" && !Array.isArray(value);
}

function readString(value: unknown): string {
    return typeof value === "string" ? value : "";
}

function readInteger(value: unknown): number | null {
    return typeof value === "number" && Number.isSafeInteger(value) ? value : null;
}

export function normalizeApiBaseUrl(value: string): string {
    let parsed: URL;
    try {
        parsed = new URL(value.trim());
    } catch {
        throw new ApiImportError("invalid-url", "The API address is invalid.");
    }
    if (
        (parsed.protocol !== "http:" && parsed.protocol !== "https:") ||
        parsed.username !== "" ||
        parsed.password !== "" ||
        parsed.pathname !== "/" ||
        parsed.search !== "" ||
        parsed.hash !== ""
    ) {
        throw new ApiImportError("invalid-url", "The API address must be an HTTP or HTTPS origin.");
    }
    return parsed.origin;
}

async function fetchApiJson(apiBaseUrl: string, path: string): Promise<unknown> {
    let response: Response;
    try {
        response = await fetch(`${apiBaseUrl}${path}`, {
            cache: "no-store",
            headers: { Accept: "application/json" },
            signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
        });
    } catch {
        throw new ApiImportError("unreachable", "The local API request failed.");
    }
    if (!response.ok) {
        throw new ApiImportError("http-error", `The local API returned HTTP ${response.status}.`);
    }
    try {
        return await response.json();
    } catch {
        throw new ApiImportError("invalid-response", "The local API response is not valid JSON.");
    }
}

async function loadCollection(
    apiBaseUrl: string,
    path: string,
    limit: number,
): Promise<readonly Record<string, unknown>[]> {
    const items: Record<string, unknown>[] = [];
    for (let page = 0; page <= Math.ceil(limit / PAGE_SIZE); page += 1) {
        const payload = await fetchApiJson(apiBaseUrl, `${path}?limit=${PAGE_SIZE}&offset=${items.length}`);
        if (!isRecord(payload) || !Array.isArray(payload.items)) {
            throw new ApiImportError("invalid-response", `Invalid collection response for ${path}.`);
        }
        const records = payload.items;
        const total = readInteger(payload.total) ?? records.length;
        if (total < 0 || total > limit) {
            throw new ApiImportError("too-large", `Collection ${path} exceeds the import limit.`);
        }
        if (records.length > PAGE_SIZE || records.some((record) => !isRecord(record))) {
            throw new ApiImportError("invalid-response", `Invalid collection items for ${path}.`);
        }
        items.push(...(records as Record<string, unknown>[]));
        if (items.length >= total) {
            return items;
        }
        if (records.length === 0) {
            throw new ApiImportError("invalid-response", `Collection ${path} ended before its declared total.`);
        }
    }
    throw new ApiImportError("too-large", `Collection ${path} exceeds the import limit.`);
}

function readRack(record: Record<string, unknown>): CsimRack | null {
    const gameId = readInteger(record.id);
    const capacityU = readInteger(record.capacity_u);
    const floorSlot = record.floor_slot === null ? null : readInteger(record.floor_slot);
    const wallMount = record.wall_mount === true;
    if (
        gameId === null ||
        gameId < 0 ||
        capacityU === null ||
        capacityU < 1 ||
        capacityU > 42 ||
        record.pending_placement === true ||
        (!wallMount && floorSlot === null)
    ) {
        return null;
    }
    return {
        gameId,
        name: readString(record.name),
        capacityU,
        floorSlot,
        wallMount,
    };
}

function readEquipment(record: Record<string, unknown>, kind: Exclude<SaveEquipmentKind, "edd">): CsimEquipment | null {
    const gameId = readInteger(record.id);
    const gameRackId = readInteger(record.rack_id);
    const startU = readInteger(record.start_u);
    const sizeU = readInteger(record.u_size);
    const skuName = readString(record.sku_name);
    if (
        (record.inv_status !== undefined && record.inv_status !== "Mounted") ||
        gameId === null ||
        gameRackId === null ||
        startU === null ||
        sizeU === null ||
        gameId < 0 ||
        gameRackId < 0 ||
        startU < 1 ||
        sizeU < 1 ||
        sizeU > 42 ||
        skuName === ""
    ) {
        return null;
    }
    return {
        gameId,
        kind,
        displayName: readString(record.display_name),
        skuName,
        sizeU,
        gameRackId,
        startU,
    };
}

export function createDecodedSaveFromApiSnapshot(snapshot: ApiSnapshot): DecodedCsimSave {
    const racks = snapshot.racks.map(readRack).filter((rack): rack is CsimRack => rack !== null);
    const equipment: CsimEquipment[] = [];
    for (const definition of API_COLLECTIONS) {
        const records = snapshot.equipment[definition.kind] ?? [];
        for (const record of records) {
            const item = readEquipment(record, definition.kind);
            if (item !== null) {
                equipment.push(item);
            }
        }
    }
    if (snapshot.siteId.trim() === "" || racks.length === 0) {
        throw new ApiImportError("invalid-response", "The API snapshot has no active site or installed racks.");
    }
    if (equipment.length > MAX_API_ITEMS) {
        throw new ApiImportError("too-large", "The API snapshot exceeds the equipment limit.");
    }
    return { version: 0, siteId: snapshot.siteId, racks, equipment };
}

export async function importPlanFromApi(
    apiUrl: string,
    onProgress: (message: ApiImportProgressMessage) => void,
): Promise<SavegamePlanResult> {
    const apiBaseUrl = normalizeApiBaseUrl(apiUrl);
    onProgress({ type: "progress", stage: "connecting", percent: 5 });
    const world = await fetchApiJson(apiBaseUrl, "/api/v1/world");
    if (!isRecord(world) || !isRecord(world.topbar)) {
        throw new ApiImportError("invalid-response", "The world response has no topbar metadata.");
    }
    const siteId = readString(world.topbar.current_site_id);
    const siteName = readString(world.topbar.current_site_name);
    onProgress({ type: "progress", stage: "world", percent: 15 });

    const racks = await loadCollection(apiBaseUrl, "/api/v1/racks", MAX_API_RACKS);
    onProgress({ type: "progress", stage: "racks", percent: 28 });
    const equipment: Partial<Record<Exclude<SaveEquipmentKind, "edd">, readonly Record<string, unknown>[]>> = {};
    let equipmentCount = 0;
    for (let index = 0; index < API_COLLECTIONS.length; index += 1) {
        const definition = API_COLLECTIONS[index];
        if (definition === undefined) {
            throw new ApiImportError("invalid-response", "The API collection list is invalid.");
        }
        const records = await loadCollection(apiBaseUrl, definition.path, MAX_API_ITEMS);
        equipmentCount += records.length;
        if (equipmentCount > MAX_API_ITEMS) {
            throw new ApiImportError("too-large", "The combined equipment inventory exceeds the import limit.");
        }
        equipment[definition.kind] = records;
        onProgress({
            type: "progress",
            stage: "equipment",
            percent: 30 + Math.round(((index + 1) / API_COLLECTIONS.length) * 50),
        });
    }

    onProgress({ type: "progress", stage: "mapping", percent: 88 });
    const decoded = createDecodedSaveFromApiSnapshot({ siteId, siteName, racks, equipment });
    try {
        return createPlanFromApiSnapshot(decoded, siteName);
    } catch (reason) {
        if (reason instanceof SavegameImportError && /site/i.test(reason.message)) {
            throw new ApiImportError("unsupported-site", reason.message);
        }
        throw new ApiImportError("mapping-failed", "The API snapshot could not be mapped to a plan.");
    }
}
